import classNames from 'classnames';
import React from 'react';

import * as Keyboard from 'util/Keyboard';
import {StageConfiguration} from 'workflows/WorkflowConstants';

import {GraphLayout} from './GraphLayout';
import {STAGE_RUN_RADIUS} from './GraphLayoutConstants';
import {HighlightMode} from './StageFlowNodeHighlightUtil';
import StageFlowNodeVisualization, {
  StageFlowNodeSecondaryLinkVisualization,
} from './StageFlowNodeVisualization';
import {StageRunLinkVisualization} from './StageRunVisualization';
import StageVisualization from './StageVisualization';
import {StageFlowNodeGraph, StageRunGraph} from './TaggedNodeGraph';
import {VirtualNodeGraph} from './VirtualNodeGraph';

interface VirtualNodeGraphVisualizationProps {
  containerWidth: number;
  clickableNodeIds: Array<string>;
  // Existing nodes currently being edited in the visualization
  nodeIdsToEdit: Array<string>;
  // Null is passed in as an argument to indicate clicking on the background
  onClickNode: (nodeId: string | null) => void;
  // Null is passed in as an argument to indicate clicking on the background
  onCtrlClickNode: (nodeId: string | null) => void;
  virtualNodeGraph: VirtualNodeGraph<StageRunGraph | StageFlowNodeGraph>;
  stageConfigurations: Array<StageConfiguration>;
}

class VirtualNodeGraphVisualization extends React.Component<VirtualNodeGraphVisualizationProps> {
  _handleClick(e: React.MouseEvent<HTMLElement | SVGGElement>, nodeId: string | null): void {
    // Stop the event from bubbling to the background div
    e.stopPropagation();
    if (!nodeId || this.props.clickableNodeIds.includes(nodeId)) {
      if (Keyboard.isOSCtrlKey(e)) {
        this.props.onCtrlClickNode(nodeId);
      } else {
        this.props.onClickNode(nodeId);
      }
    }
  }

  _renderStages(graphLayout: GraphLayout): JSX.Element {
    const totalHeight = graphLayout.getTotalHeight(this.props.containerWidth);
    return (
      <div className="workflowVisualization-stages">
        {this.props.stageConfigurations.map((stageConfiguration) => (
          <StageVisualization
            key={stageConfiguration.id}
            name={stageConfiguration.stageName}
            height={totalHeight}
            width={graphLayout.getTagWidth(stageConfiguration.index)}
          />
        ))}
      </div>
    );
  }

  _renderLinks(graphLayout: GraphLayout): Array<JSX.Element> {
    const totalWidth = graphLayout.getTotalWidth(this.props.containerWidth);
    const stageFlowNodeLinks = graphLayout.getLinks({
      leftBoundary: 0,
      rightBoundary: totalWidth,
    });
    const virtualNodeIds = this.props.virtualNodeGraph.getVirtualNodeIds();
    const relevantNodeIds = [...virtualNodeIds, ...this.props.nodeIdsToEdit];
    return stageFlowNodeLinks.map((link) => {
      const {key, leftPosition, rightPosition, parentNodeId, childNodeId, isSecondary} = link;
      const StageRunLinkComponent = isSecondary
        ? StageFlowNodeSecondaryLinkVisualization
        : StageRunLinkVisualization;
      return (
        <StageRunLinkComponent
          key={key}
          highlightMode={HighlightMode.DEFAULT}
          isDiscarded={!relevantNodeIds.includes(parentNodeId!) && !relevantNodeIds.includes(childNodeId!)}
          leftPosition={leftPosition}
          rightPosition={rightPosition}
        />
      );
    });
  }

  _renderNodes(graphLayout: GraphLayout): Array<JSX.Element> {
    const {nodeIdsToEdit, virtualNodeGraph} = this.props;
    const virtualNodeIds = virtualNodeGraph.getVirtualNodeIds();
    const relevantNodeIds = [...virtualNodeIds, ...nodeIdsToEdit];
    return virtualNodeGraph.getNodeIds().map((nodeId) => {
      const position = graphLayout.getNodePosition(nodeId);
      const tag = virtualNodeGraph.getTag(nodeId);
      const stageWidth = graphLayout.getTagWidth(tag);
      const isVirtual = virtualNodeIds.includes(nodeId);
      const childNodes = [
        ...virtualNodeGraph.getPrimaryChildNodeIds(nodeId),
        ...virtualNodeGraph.getSecondaryChildNodeIds(nodeId),
      ];
      const isParentOfRelevantNode = childNodes.some((childNodeId) => relevantNodeIds.includes(childNodeId));
      return (
        <StageFlowNodeVisualization
          key={nodeId}
          className={classNames({
            // Default styling includes virtual nodes
            'stageFlowNodeVisualization--default': relevantNodeIds.includes(nodeId),
            'stageFlowNodeVisualization--orange': !isVirtual && isParentOfRelevantNode,
            'stageFlowNodeVisualization--gray':
              !isVirtual && !isParentOfRelevantNode && !nodeIdsToEdit.includes(nodeId),
          })}
          experimentalConditionValues={virtualNodeGraph.getExperimentalConditionValues(nodeId)}
          highlightMode={HighlightMode.DEFAULT}
          isNodeClickable={this.props.clickableNodeIds.includes(nodeId)}
          onClick={(e) => {
            this._handleClick(e, nodeId);
          }}
          position={position}
          relativeX={graphLayout.getRelativeX(nodeId)}
          shouldShowExperimentalCondition={virtualNodeGraph.isLastDescendantWithTag(nodeId)}
          stageRunStatus={virtualNodeGraph.getStatus(nodeId)}
          stageWidth={stageWidth}
          tooltipTitle={virtualNodeGraph.getName(nodeId)}
        />
      );
    });
  }

  override render(): JSX.Element {
    const graphLayout = new GraphLayout(this.props.virtualNodeGraph, STAGE_RUN_RADIUS);
    const treeHeight = graphLayout.getTreeHeight();
    const totalWidth = graphLayout.getTotalWidth(this.props.containerWidth);
    const totalHeight = graphLayout.getTotalHeight(this.props.containerWidth);
    return (
      <div
        onClick={(e) => {
          this._handleClick(e, null);
        }}
        style={{width: totalWidth, height: totalHeight}}
      >
        {this._renderStages(graphLayout)}
        <div
          className={classNames('workflowVisualization-arms', {
            'is-tall': graphLayout.isTallVisualization(),
          })}
        >
          <svg width={totalWidth} height={treeHeight}>
            {this._renderLinks(graphLayout)}
            {this._renderNodes(graphLayout)}
          </svg>
        </div>
      </div>
    );
  }
}

export default VirtualNodeGraphVisualization;
